import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { PricingSelector } from "@/components/PricingSelector";
import { Seo } from "@/components/Seo";
import { INCLUDED, NETWORK_NOTE } from "@/lib/content";

export default function Offres() {
  return (
    <>
      <Seo title="Tarifs de l'infogérance informatique pour TPE" description="Trois forfaits tout compris selon la taille de votre équipe : 397 € HT/mois de 1 à 3 postes, 497 € de 4 à 6 postes, 697 € de 7 à 9 postes. Zéro frais de mise en service, option ordinateurs à 60 € HT par poste et par mois." path="/offres" />

      <section className="bg-navy grain relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-[-8%] w-[42rem] h-[42rem] rounded-full bg-amber-500/10 blur-[150px]" aria-hidden />
        <div className="pointer-events-none absolute bottom-0 left-[-10%] w-[34rem] h-[34rem] rounded-full bg-[#0b264e]/60 blur-[130px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-500">Nos offres</span></Reveal>
          <Reveal delay={0.05}><h1 className="mt-5 font-display text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter text-white max-w-4xl text-balance">Trois forfaits, une seule promesse : la tranquillité.</h1></Reveal>
          <Reveal delay={0.1}><p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">Un prix tout compris selon la taille de votre équipe, sans frais de départ : la même protection pour toutes les entreprises, sans devis à décortiquer.</p></Reveal>
        </div>
      </section>

      {/* Bloc tarifaire — même offre que sur la page d'accueil */}
      <section className="bg-navy-dark grain relative py-24 md:py-28" data-testid="offres-formules">
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white text-balance">Un tarif simple, adapté à votre équipe.</h2>
            <p className="mt-4 text-slate-300">Choisissez votre forfait selon le nombre de personnes : tout est compris, avec un budget prévisible.</p>
          </div>
          <PricingSelector id="tarifs-offres" />
        </div>
      </section>

      {/* Ce qui est compris */}
      <section className="bg-[#f8fafc] py-24 md:py-28 border-y border-slate-200" data-testid="offres-inclus">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Ce qui est compris</span>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy">Tout est inclus, sans exception.</h2>
          </div>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
            {INCLUDED.map((it) => (
              <StaggerItem key={it} className="flex items-start gap-3 text-slate-700"><Check className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" /><span>{it}</span></StaggerItem>
            ))}
          </Stagger>
          <p className="mt-8 text-sm text-slate-500 max-w-3xl leading-relaxed">{NETWORK_NOTE}</p>
          <div className="mt-10">
            <Link to="/ce-que-vous-obtenez" className="inline-flex items-center gap-2 font-medium text-navy link-underline" data-testid="offres-obtenez-link">
              Voir concrètement ce que vous obtenez <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Mise en service détaillée */}
      <section className="bg-[#f8fafc] py-24 md:py-28 border-t border-slate-200" data-testid="offres-mise-en-service">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">La mise en service</span>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy">Un démarrage sans effort de votre part</h2>
          </div>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {["État des lieux complet de votre installation et de vos usages", "Installation et sécurisation de tous les postes", "Configuration des clés de sécurité pour chaque personne", "Sauvegarde et protection de vos données", "Sensibilisation de vos équipes aux bons réflexes", "Accompagnement rapproché au démarrage"].map((t, i) => (
              <StaggerItem key={i} className="bg-white border border-slate-200 rounded-xl p-7"><span className="font-display text-amber-600 font-semibold">0{i + 1}</span><p className="mt-3 text-navy leading-snug">{t}</p></StaggerItem>
            ))}
          </Stagger>
          <div className="mt-10 p-6 rounded-xl border border-slate-200 bg-white max-w-3xl">
            <p className="text-navy"><strong>La mise en service est incluse dans votre forfait :</strong> aucun frais de départ, le matériel et l'installation sont compris. Tout est pris en charge par notre équipe, de l'état des lieux à la bascule — sans surprise après signature.</p>
          </div>
        </div>
      </section>
    </>
  );
}
